
//* Hooks Imports
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm, Path } from 'react-hook-form';
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

//* Libraries Imports
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from "framer-motion";

//* Components Imports
import { Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ErrorMessages } from "../../../../../component/common/ErrorMessages";
import { ResourceQuestionSkeleton } from '../../../../../skeleton/ResourceQuestionSkeleton/resource-question-skeleton';
import {
  HeaderInfo,
  ExamSelection,
  DescriptionFields,
  DeadlineInfo,
  CandidateInfo,
  FileUpload,
  ActionButtons,
} from "./forms";

//* Custom Hooks Imports
import { useGetProcessById } from '../../../../../hooks/queries/process/use-get-process';
import { useGetProcessExamFilters } from '../../../../../hooks/queries/process/use-get-process-exam-filters';
import { useStoreResourceQuestion } from '../../../../../hooks/mutations/resource-question/use-store-resource-question';
import { useUpdateResourceQuestion } from '../../../../../hooks/mutations/resource-question/use-update-resource-question';


//* Schemas Imports
import { formResourceQuestionSchema, formUpdateResourceQuestionSchema, type CreateResourceQuestion, type FullResourceQuestion, type UpdateResourceQuestion } from '../../../../../schemas/common/resource/question/resource-question';

// * Animations Imports
import { containerVariants } from './animations';

// ? Styles Imports
import './resource-question-form.scss';


type FormDataType = (CreateResourceQuestion | UpdateResourceQuestion) & {
  files_exists?: string[];
  processo_id?: number;
};

type ResourceQuestionFormProps = {
  resourceQuestionData?: FullResourceQuestion;
  isLoadingResourceQuestion?: boolean;
  isErrorResourceQuestion?: boolean;
  isEditMode?: boolean;
}

export function ResourceQuestionForm({ 
  resourceQuestionData, 
  isLoadingResourceQuestion, 
  isErrorResourceQuestion,
  isEditMode = false
}: ResourceQuestionFormProps) {  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const processId = id ? parseInt(id) : 0;
  const processQuery = useGetProcessById(processId);
  const examFiltersQuery = useGetProcessExamFilters(processId);
  const userData = useSelector((state: any) => state.auth.me);
  const queryClient = useQueryClient();const createMutation = useStoreResourceQuestion();
  const updateMutation = useUpdateResourceQuestion();  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);const [submitError, setSubmitError] = useState<string | null>(null);
  const [resourceNumber] = useState(() => Math.floor(Math.random() * 10000) + 1000);

  const existingFiles = useMemo(() => {
    if (!isEditMode || !resourceQuestionData?.files) return [];
    return resourceQuestionData.files
      .filter(file => !removedFileIds.includes(file.id))
      .map(file => ({
        id: file.id,
        name: file.name,
        path: file.path
      }));
  }, [isEditMode, resourceQuestionData?.files, removedFileIds]);

  const defaultFormValues = useMemo(() => {
    const collegeId = resourceQuestionData?.college?.id || processQuery.data?.data?.college?.id || 0;
      return {
        name:
          resourceQuestionData?.name || `Recurso de Questão ${resourceNumber}`,
        processo_id: isEditMode ? undefined : processId,
        faculdade_id: collegeId,
        prova_id: resourceQuestionData?.test?.id || 0,
        categoria_id: resourceQuestionData?.categoria_id
          ? Number(resourceQuestionData.categoria_id)
          : 0,
        questao_id: resourceQuestionData?.question?.id || 0,
        description: resourceQuestionData?.description || "",
        fundamentacao: resourceQuestionData?.rationale || "",
        pedido_realizado: resourceQuestionData?.requestMade || "",
        numero_inscricao_candidato:
          resourceQuestionData?.candidateRegistrationNumber ||
          userData?.numeroInscricao ||
          "",
        data_limite: resourceQuestionData?.deadline
          ? new Date(resourceQuestionData.deadline.slug)
              .toISOString()
              .split("T")[0]
          : new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0],
        files_exists:
          isEditMode && resourceQuestionData?.files
            ? resourceQuestionData.files.map((file) => file.id)
            : undefined,
      };
  }, [resourceQuestionData, processQuery.data, isEditMode, processId, resourceNumber, userData?.numeroInscricao]);  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormDataType>({
    resolver: zodResolver(isEditMode ? formUpdateResourceQuestionSchema : formResourceQuestionSchema),
    defaultValues: defaultFormValues,
    mode: 'onChange'
  });
  // UseEffect for resetting form values
  useEffect(() => {
    reset(defaultFormValues);
  }, [reset, defaultFormValues]);
  // useEffect for setting numero_inscricao_candidato
  useEffect(() => {
    if (userData?.numeroInscricao && !watch('numero_inscricao_candidato')) {
      setValue('numero_inscricao_candidato', userData.numeroInscricao);
    }
  }, [userData?.numeroInscricao, setValue, watch]);

  const deadlineInfo = useMemo(() => {
    const deadlineDate = new Date();
    deadlineDate.setDate(deadlineDate.getDate() + 2);
    return {
      formattedDate: deadlineDate.toLocaleDateString('pt-BR'),
      formattedTime: deadlineDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    };
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      
      const oversizedFiles = newFiles.filter(file => file.size > 5 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        setUploadError('Alguns arquivos excedem o tamanho máximo de 5MB.');
        return;
      }
      
      // Check if the total number of files exceeds 10
      const totalFiles = uploadedFiles.length + newFiles.length + existingFiles.length;
      if (totalFiles > 10) {
        setUploadError('Você pode ter no máximo 10 arquivos no total (novos + existentes).');
        return;
      }
      
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  }, [uploadedFiles.length, existingFiles.length]);

  const removeFile = useCallback((index: number) => {
    setUploadedFiles(files => files.filter((_, i) => i !== index));
  }, []);
  const removeExistingFile = useCallback((fileId: string) => {
    setRemovedFileIds(prev => [...prev, fileId]);
  }, []);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const setValueAdapter = useCallback(<K extends Path<FormDataType>>(field: K, value: any) => {
    setValue(field, value, { shouldValidate: true });
  }, [setValue]);  
    const onSubmit = (data: FormDataType) => {
    setIsSubmitting(true);
    setSubmitError(null);

    if (isEditMode && resourceQuestionData?.id) {
      const updateData = {
        ...data,
        files_exists: existingFiles.map(file => file.id)
      } as UpdateResourceQuestion;

      updateMutation.mutate({
        processoId: processId.toString(),
        questionId: resourceQuestionData.id,
        data: updateData,
        files: uploadedFiles,
      }, {        
        onSuccess: () => {         
          setIsSuccess(true);
          toast.success("Recurso atualizado com sucesso!");
          
          // Navigate after brief success state
          setTimeout(() => {
            navigate(-1);
            setIsSubmitting(false);
            setIsSuccess(false);
          }, 500);

          // Background cache updates (non-blocking)
          setTimeout(() => {
            queryClient.refetchQueries({
              queryKey: ["paginated-resource-question"],
            });
            queryClient.invalidateQueries({
              queryKey: ["resource-question"],
            });
          }, 100);
        },        onError: (error: any) => {
          console.error("Erro ao atualizar o recurso:", error);
          setSubmitError(
            "Ops! Algo deu errado. Por favor, tente novamente."
          );
        },
        onSettled: () => {
          // Only reset submitting if not successful (to maintain success state briefly)
          if (!isSuccess) {
            setIsSubmitting(false);
          }
        }
      });
    } else {
      createMutation.mutate({
        processoId: processId.toString(),
        data: data as CreateResourceQuestion,
        files: uploadedFiles,
      }, {        onSuccess: () => {          
          setIsSuccess(true);
          toast.success("Recurso enviado com sucesso!");
          
          // Navigate after brief success state
          setTimeout(() => {
            navigate(-1);
            setIsSubmitting(false);
            setIsSuccess(false);
          }, 500);

          // Background cache updates (non-blocking)
          setTimeout(() => {
            queryClient.refetchQueries({
              queryKey: ["paginated-resource-question"],
            });
          }, 100);
        },        onError: (error: any) => {
          console.error("Erro ao criar o recurso:", error);
          setSubmitError(
            "Ops! Algo deu errado. Por favor, tente novamente."
          );
        },
        onSettled: () => {
          // Only reset submitting if not successful (to maintain success state briefly)
          if (!isSuccess) {
            setIsSubmitting(false);
          }
        }
      });
    }
  };if (processQuery.isLoading || isLoadingResourceQuestion || examFiltersQuery.isLoading) {
    return (
      <ResourceQuestionSkeleton isEditMode={isEditMode} />
    );
  }

  if (processQuery.isError || !processQuery.data?.success || !processQuery.data?.data) {
    return (      
    <ErrorMessages 
        messages={["Não foi possível carregar as informações. Por favor, tente novamente."]} 
        clearErrors={() => navigate(-1)}
      />
    );
  }

  if (examFiltersQuery.isError || !examFiltersQuery.data?.success) {
    return (      
    <ErrorMessages 
        messages={["Não foi possível carregar as provas. Por favor, tente novamente."]} 
        clearErrors={() => navigate(-1)}
      />
    );
  }

  if (isErrorResourceQuestion) {
    return (      
    <ErrorMessages 
        messages={["Não foi possível carregar as informações. Por favor, tente novamente."]} 
        clearErrors={() => navigate(-1)}
      />
    );
  }
  const process = processQuery.data.data;
  const examFilters = examFiltersQuery.data?.data;
  return (
    <motion.div
      className="resource-question-form-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <h4 className="mb-0">
            {isEditMode ? 'Editar Recurso de Questão' : 'Solicitação de Recurso de Questão'}
          </h4>
        </Card.Header>
        <Card.Body className={isSubmitting || isSuccess ? 'processing' : ''}>

          <form onSubmit={handleSubmit(onSubmit)}>
            {submitError && (
              <div className="alert alert-danger mb-4" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {submitError}
              </div>
            )}
            
            {isSuccess && (
              <div className="alert alert-success mb-4" role="alert">
                <i className="bi bi-check-circle-fill me-2"></i>
                {isEditMode ? 'Recurso atualizado com sucesso! Redirecionando...' : 'Recurso enviado com sucesso! Redirecionando...'}
              </div>
            )}
            
            <HeaderInfo
              processName={process.name}
              resourceName={watch("name") || `Recurso de Questão ${resourceNumber}`}
              register={register}
              errors={errors}
            />

            <CandidateInfo 
              register={register} 
              disabled={isEditMode}
            />            
            
            <DeadlineInfo
              date={watch("data_limite") || deadlineInfo.formattedDate}
              time={deadlineInfo.formattedTime}
              register={register}
              errors={errors}
              setValue={setValueAdapter}
              disabled={isEditMode}
            />            
                
                
            <ExamSelection
              control={control}
              setValue={setValueAdapter}
              errors={errors}
              examFilters={examFilters || undefined}
              disabled={isEditMode}
            />

            <DescriptionFields
              register={register}
              errors={errors}
            />            
            
            <FileUpload
              uploadedFiles={uploadedFiles}
              existingFiles={existingFiles}
              uploadError={uploadError}
              onFileChange={handleFileChange}
              onRemoveFile={removeFile}
              onRemoveExistingFile={removeExistingFile}
              isEditMode={isEditMode}
            />            
            <ActionButtons
              isSubmitting={isSubmitting}
              isSuccess={isSuccess}
              onCancel={handleCancel}
              isEditMode={isEditMode}
            />
          </form>
        </Card.Body>
      </Card>
    </motion.div>
  );
}
